// Log in as user: this browser's remembered UI state while an admin views as someone (see viewAs.ts).
import type { CurrentUser } from './api';

/**
 * While viewing as someone, this browser's localStorage reads and writes a
 * namespace of its own, so the person's view starts from nothing remembered
 * rather than the admin's recent records, list views and tabs, and the
 * admin's own are untouched when they exit. Installed once, before the app
 * renders; a reload (which every start and exit does) removes it.
 */
export function separateViewAsStorage(user: CurrentUser): void {
  if (!user.impersonatedBy || typeof window === 'undefined') return;
  let base: Storage;
  try {
    base = window.localStorage;
  } catch {
    return;
  }
  const prefix = `view-as:${user.email}:`;
  const own = (): string[] => {
    const keys: string[] = [];
    for (let i = 0; i < base.length; i += 1) {
      const key = base.key(i);
      if (key !== null && key.startsWith(prefix)) keys.push(key);
    }
    return keys;
  };
  const scoped: Storage = {
    get length() {
      return own().length;
    },
    key: (index) => own()[index]?.slice(prefix.length) ?? null,
    getItem: (key) => base.getItem(prefix + key),
    setItem: (key, value) => base.setItem(prefix + key, value),
    removeItem: (key) => base.removeItem(prefix + key),
    clear: () => own().forEach((key) => base.removeItem(key)),
  };
  Object.defineProperty(window, 'localStorage', { configurable: true, get: () => scoped });
}
